<template>
    <div id="chatWindow" :key="renderCount">
        <VisitorBanModal
            :visitor="activeConversation.visitor"
            :ban-modal-visible="visitorBanModal"
            @close="visitorBanModal = false"
        />
        <v-tour :callbacks="tourCallbacks" :steps="steps" name="chat-window-tour">
            <template slot-scope="tour">
                <transition name="fade">
                    <v-step
                        v-for="(step, index) of tour.steps"
                        v-if="tour.currentStep === index"
                        :key="index"
                        :is-first="tour.isFirst"
                        :is-last="tour.isLast"
                        :labels="tour.labels"
                        :next-step="tour.nextStep"
                        :previous-step="tour.previousStep"
                        :step="step"
                        :stop="tour.stop"
                    >
                        <div slot="actions"></div>
                    </v-step>
                </transition>
            </template>
        </v-tour>
        <b-row v-if="activeConversation">
            <b-col :lg="searchVisible || contentWidgetVisible ? 6 : 8" class="pb-3" md="7">
                <div
                    class="chatContainer"
                    @dragenter="dragEnter"
                    @dragleave="dragLeave"
                    @dragover="dragOver"
                    @drop="dropBlock"
                >
                    <div id="chatCloseSettingsButton">
                        <div class="user-meta">
                            <div v-if="activeConversation.fullname">
                                <div class="d-flex align-items-center">
                                    <a-avatar
                                        :key="activeConversation._id"
                                        :size="24"
                                        :src="getVisitorAvatarSrc()"
                                        shape="square"
                                        style="background-color: #3899ff; border-radius: 7px !important"
                                    >
                                        {{ activeConversation.fullname.substr(0, 1).toLocaleUpperCase() }}
                                    </a-avatar>
                                    <span
                                        id="visitorName"
                                        ref="visitorNameInput"
                                        :content="activeConversation.fullname || activeConversation.whatsappNumber"
                                        class="darkSpan ml-1 visitorName disableShortcut"
                                        @keydown="handleVisitorNameInputKeyDown"
                                        @keyup="handleVisitorNameInputKeyUp"
                                        placeholder="Type visitor name..."
                                        >{{
                                            capitalize(activeConversation.fullname || activeConversation.whatsappNumber)
                                        }}
                                    </span>
                                    <span
                                        class="ml-2 d-flex align-items-center"
                                        style="color: #3899ff"
                                        v-if="activeConversation.email && activeConversation.email !== ''"
                                    >
                                        <a-icon class="mr-2" type="mail" />
                                        <div
                                            id="visitorEmail"
                                            ref="visitorEmailInput"
                                            :content="activeConversation.email"
                                            class="visitorEmail disableShortcut"
                                            @keydown="handleVisitorEmailInputKeyDown"
                                        >
                                            <span v-if="hasUserMail()"
                                                ><a :href="'mailto:' + activeConversation.email">{{
                                                    activeConversation.email
                                                }}</a></span
                                            >
                                            <span v-if="!hasUserMail()"
                                                ><a :href="'tel:' + getChannelNumber()">{{
                                                    getChannelNumber()
                                                }}</a></span
                                            >
                                        </div>
                                    </span>
                                </div>
                            </div>
                            <div v-else>
                                <a-avatar :size="24" shape="square">
                                    {{
                                        (getFullname()
                                            ? getFullname()
                                            : activeConversation.fullname || activeConversation.whatsappNumber || "-"
                                        )
                                            .substr(0, 1)
                                            .toLocaleUpperCase()
                                    }}
                                </a-avatar>

                                <span class="ellipsis">{{
                                    getFullname()
                                        ? getFullname()
                                        : activeConversation.fullname || activeConversation.whatsappNumber || "-"
                                }}</span>
                                <span v-if="getEmail()">✦ {{ getEmail() }}</span>
                            </div>
                        </div>
                        <div class="d-flex flex-row" style="align-items: center">
                            <button class="video-call-action-button" @click.prevent="searchVisible = !searchVisible">
                                <span class="material-icons">search</span>
                            </button>
                            <div v-if="videoCallFeatureEnabled || audioCallFeatureEnabled">
                                <a-popover
                                    v-if="
                                        !videoCallMode &&
                                        !activeConversation.whatsappNumber &&
                                        !activeConversation.instagramUserId &&
                                        !activeConversation.messengerUserId &&
                                        !activeConversation.telegramUserId
                                    "
                                    id="call-button"
                                    placement="bottomRight"
                                    trigger="click"
                                >
                                    <template slot="content">
                                        <div v-if="user">
                                            <button
                                                :disabled="videoCallMode"
                                                class="d-flex flex-row mt-3 align-items-center naked-btn"
                                                @click.prevent="call(false)"
                                                v-if="videoCallFeatureEnabled"
                                            >
                                                <VideoCallSvg style="fill: #3899ff" />
                                                <div class="justify-content-center pl-3 popoverFont cursorPointer">
                                                    {{ $t("message.startVideoCall") }}
                                                </div>
                                            </button>
                                            <button
                                                :disabled="videoCallMode"
                                                class="d-flex flex-row mt-3 align-items-center naked-btn"
                                                @click.prevent="call(true)"
                                                v-if="audioCallFeatureEnabled"
                                            >
                                                <Call style="fill: #3899ff" />
                                                <div class="justify-content-center pl-3 popoverFont cursorPointer">
                                                    {{ $t("message.startAudioCall") }}
                                                </div>
                                            </button>
                                        </div>
                                    </template>
                                    <button class="video-call-action-button">
                                        <VideoCallSvg style="fill: #3899ff" />
                                    </button>
                                </a-popover>
                                <button v-else class="video-call-action-button" @click="cancelCall()">
                                    <VideoCallCloseSvg style="fill: #f54e60" />
                                </button>
                            </div>

                            <a-popover
                                id="actionSettingsButton"
                                v-model="isPopoverVisible"
                                data-v-step="17"
                                placement="bottomRight"
                                title=""
                                trigger="click"
                            >
                                <template slot="content">
                                    <div v-if="user">
                                        <div
                                            class="d-flex flex-row"
                                            v-if="checkPermission(PERMISSION_TYPES['visitor.block'].tr.name)"
                                        >
                                            <div class="justify-content-start">
                                                <block />
                                            </div>
                                            <div
                                                v-if="
                                                    isRoot ||
                                                    user.role.permissions.map((x) => x.name).includes('visitor.block')
                                                "
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="ban"
                                            >
                                                {{ $t("message.block_") }}
                                            </div>
                                        </div>
                                        <div class="d-flex flex-row mt-3">
                                            <div class="justify-content-start">
                                                <private-message-icon />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="pm"
                                            >
                                                {{ $t("message.privateMessage_") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <assign-representative />
                                            </div>
                                            <div
                                                @click="$emit('input', 'assign-agent')"
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                            >
                                                {{ $t("message.assignRep") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <open-chat />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click="joinTheChat(activeConversation)"
                                            >
                                                {{ $t("message.joinChat") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="!observing.includes(activeConversation._id)"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <forward-representative />
                                            </div>
                                            <div
                                                @click="forwardRep"
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                            >
                                                {{ $t("message.forwardRep") }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="activeConversation.users.filter((u) => u !== null).length === 1"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <font-awesome-icon class="text-danger" icon="sign-out-alt" />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="leave"
                                            >
                                                {{ $t("message.leaveChat") }}
                                            </div>
                                        </div>
                                        <div
                                            v-else-if="activeConversation.users.filter((u) => u !== null).length >= 2"
                                            class="d-flex flex-row mt-3"
                                        >
                                            <div class="justify-content-start">
                                                <font-awesome-icon class="text-danger" icon="sign-out-alt" />
                                            </div>
                                            <div
                                                class="justify-content-end pl-3 popoverFont cursorPointer"
                                                @click.prevent="end"
                                            >
                                                {{ $t("message.endChat") }}
                                            </div>
                                        </div>
                                        <div class="d-flex flex-row mt-3" v-if="settings.autoTranslate">
                                            <div class="justify-content-start">
                                                <font-awesome-icon icon="globe" />
                                            </div>
                                            <div class="justify-content-end pl-3 popoverFont cursorPointer">
                                                {{ $t("message.autoTranslate") }} :
                                                <a-switch v-model="isSwitched" class="ml-2 mr-2" size="small" />
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <button class="video-call-action-button">
                                    <chat-window-settings></chat-window-settings>
                                </button>
                            </a-popover>
                            <button class="video-call-action-button" data-v-step="18" @click.prevent="hide">
                                <minimize-window />
                            </button>
                            <button
                                v-if="
                                    !observing.includes(activeConversation._id) &&
                                    activeConversation.status !== 'destroyed'
                                "
                                v-tippy="{
                                    followCursor: true,
                                    placement: 'bottom-end',
                                    theme: isDarkMode ? 'light-border' : 'google',
                                }"
                                :content="
                                    activeConversation.users.filter((u) => u !== null).length === 1
                                        ? $t('message.endConversation')
                                        : $t('message.leaveChat')
                                "
                                class="video-call-action-button"
                                data-v-step="19"
                                @click.prevent="closeButton"
                            >
                                <power-off></power-off>
                            </button>
                            <button
                                v-if="
                                    observing.includes(activeConversation._id) &&
                                    activeConversation.status !== 'destroyed'
                                "
                                :disabled="!(activeConversation.status !== 'destroyed')"
                                class="video-call-action-button"
                                data-v-step="closeButton"
                                @click.prevent="leaveConversation(activeConversation._id)"
                            >
                                <circle-close-button />
                            </button>
                        </div>
                    </div>
                    <div v-if="isTranslationRequired()" class="translate-detected" @click="isSwitched = true">
                        {{
                            $t("message.autoTranslateDetectedDesc", {
                                visitorLanguage: !!languages.find(
                                    (x) => x.code === activeConversation.visitor.preferredLang
                                )
                                    ? languages.find((x) => x.code === activeConversation.visitor.preferredLang)
                                          .originalName
                                    : activeConversation.visitor.preferredLang,
                            })
                        }}
                    </div>
                    <div v-if="messageSyncRunning" class="bg-light mt-2">
                        <div class="d-flex w-100 justify-content-start mt-1">
                            <a-skeleton active avatar :paragraph="{ rows: 4 }" style="z-index: 9" />
                        </div>
                        <div class="d-flex w-100 justify-content-end mt-2">
                            <a-skeleton active avatar :paragraph="{ rows: 4 }" style="z-index: 9" />
                        </div>
                    </div>
                    <div
                        @paste="handleTPaste"
                        style="position: relative"
                        class="message-list-wrapper"
                        :class="{
                            'mail-mode': mailMode,
                            'mail-enabled': emailFeatureEnabled(),
                            'typing-mode': !!typingMessage,
                            'observing-mode': observing.includes(activeConversation._id),
                        }"
                    >
                        <MessageList
                            v-if="!dragMode && !messageSyncRunning"
                            :always-scroll-to-bottom="true"
                            :colors="colors"
                            :is-colored="true"
                            :ischatwindow="true"
                            :messages="messageSyncRunning ? [] : getMessages()"
                            :participants="getParticipants()"
                            :scroll-top="false"
                            :is-whatsapp="!!activeConversation.whatsappNumber"
                            :channel-id="activeConversation.channel"
                        />
                        <div v-else class="dnd" :style="!dragMode ? 'display:none' : ''">
                            <h1 style="align-items: center; color: grey">Choose a file or drag it here!</h1>
                        </div>
                    </div>
                    <div v-if="emailFeatureEnabled() && !observing.includes(activeConversation._id)" class="send-types">
                        <div
                            class="type"
                            :class="!mailMode && !smsMode ? 'selected' : ''"
                            @click="() => switchMode('chat')"
                        >
                            <div class="material-icons icon">chat</div>
                            <div class="send-type" @click="">Chat</div>
                        </div>
                        <div
                            class="type"
                            style="margin-left: 5px"
                            :class="mailMode ? 'selected' : ''"
                            @click="() => switchMode('email')"
                        >
                            <div class="material-icons icon">mail</div>
                            <div class="send-type">Email</div>
                        </div>
                        <div
                            class="type"
                            style="margin-left: 5px"
                            :class="smsMode ? 'selected' : ''"
                            @click="() => switchMode('sms')"
                        >
                            <div class="material-icons icon">sms</div>
                            <div class="send-type">SMS</div>
                        </div>
                    </div>
                    <div
                        :class="{ live: typingMessage !== '' }"
                        class="message typing"
                        v-if="typingMessage !== ''"
                        @dragenter="dragEnter"
                        @dragleave="dragLeave"
                        @dragover="dragOver"
                        @drop="dropBlock"
                        @paste="handleTPaste"
                    >
                        <div v-if="typingMessage !== ''" class="content p-0">
                            <div class="text" style="padding: 0 1rem">
                                <span
                                    v-tippy="{
                                        followCursor: true,
                                        placement: 'top-center',
                                        theme: isDarkMode ? 'light-border' : 'google',
                                    }"
                                    :content="typingMessage"
                                    class="quote white-space-pre-line"
                                >
                                    {{ activeConversation.fullname }} {{ $t("message.typing") }}: {{ typingMessage }}...
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="uploading-wrapper" v-if="show">
                        <span> {{ uploadPercentage }}% uploaded... please wait</span>
                        <a-progress :showInfo="false" :percent="uploadPercentage" status="active" />
                    </div>
                    <div
                        v-if="(waitingTabShortcut || isHashShortcutMode) && suggestions.length > 0"
                        id="suggestions"
                        ref="toolbarChat"
                        autofocus
                        @blur="blurSCpanel"
                        @keydown="catchShortcut"
                    >
                        <ul>
                            <li
                                v-for="(subContent, index) in suggestions.filter((x) => x.isActive)"
                                :key="subContent._id"
                                :class="{
                                    active: index === suggestionsCurrent,
                                }"
                                :data-id="subContent._id"
                                :data-letter="(subContent.letter || '').toLowerCase()"
                                :data-number="(subContent.number || '').toLowerCase()"
                                :data-hash="'#' + subContent.hash"
                                class="pointer tab"
                                @click="onShortcutSelected(subContent.languageContent[$i18n.locale].content)"
                            >
                                <div v-if="isHashShortcutMode">
                                    #{{ getLangContent(subContent.languageContent, $i18n.locale).name }}

                                    <p
                                        class="d-none"
                                        v-html="getLangContent(subContent.languageContent, $i18n.locale)"
                                    />
                                </div>
                                <div v-else>
                                    {{ getLangContent(subContent.languageContent, $i18n.locale).name }}

                                    <span class="pointer-label">{{ subContent.letter }}+{{ subContent.number }}</span>
                                    <p
                                        class="d-none"
                                        v-html="getLangContent(subContent.languageContent, $i18n.locale)"
                                    />
                                </div>
                            </li>
                        </ul>
                    </div>
                    <AIAssistant
                        v-if="copilotEnable"
                        :messages="messages"
                        :conversation-id="activeConversation._id"
                        @select="message = $event"
                    />

                    <div
                        class="reply-to-message"
                        v-if="replyToMessage"
                        :style="{ marginBottom: mailMode ? '30px' : '' }"
                    >
                        <MessageViewer :message="replyToMessage" :colors="colors" />
                    </div>
                    <SMSInput
                        v-if="smsMode"
                        :default-phone-number="activeConversation.whatsappNumber"
                        @sendSMS="sendSMS"
                    />
                    <form
                        v-if="
                            !smsMode &&
                            !observing.includes(activeConversation._id) &&
                            activeConversation.status !== 'destroyed'
                        "
                        id="frmChat"
                        autocomplete="off"
                        class="d-flex justify-content-between align-items-center bg-white"
                        style="position: relative; margin-top: auto; height: 117px; overflow: hidden"
                        :style="dragMode ? 'height: 115px' : ''"
                        @keydown="catchShortcut"
                        @submit.prevent="sendMessage"
                    >
                        <LocationShare
                            v-if="
                                customerLocationEnabled &&
                                !observing.includes(activeConversation._id) &&
                                activeConversation.status !== 'destroyed'
                            "
                        />
                        <md-button
                            style="position: absolute; top: 0; right: 74px; margin: 0; width: 24px; min-width: 24px"
                            @click="selectFile"
                            v-if="agentFileSendEnabled"
                        >
                            <md-icon>attachment</md-icon>
                        </md-button>
                        <md-button
                            id="send-message-button"
                            style="position: absolute; top: 0; right: 30px; margin: 0; width: 24px; min-width: 24px"
                            tabindex="1"
                            type="submit"
                            variant="none"
                            :class="isSendBtnActive ? '' : 'sendBtnPassive'"
                        >
                            <send-icon></send-icon>
                        </md-button>
                        <div class="emoji-icon-container">
                            <EmojiButton :onEmojiPicked="addEmojiToMessage" class="emoji-button"></EmojiButton>
                        </div>
                        <div class="voice-container">
                            <VoiceRecorder
                                v-if="
                                    agentVoiceRecordEnabled &&
                                    !observing.includes(activeConversation._id) &&
                                    activeConversation.status !== 'destroyed'
                                "
                                :cb="sendVoiceMessage"
                            />
                        </div>

                        <ckeditor
                            id="chat-editor"
                            :key="'ckeditor-' + $i18n.locale"
                            :ref="`ckeditor-${activeConversation._id}`"
                            v-model="messageModel"
                            :config="editorConfig()"
                            :editor="editor"
                            class="border-0 w-100"
                            tag-name="textarea"
                            @ready="onEditorReady"
                        />
                        <MicButton
                            :active="speechRecognitionActive"
                            @click="speechRecognitionActive = !speechRecognitionActive"
                        />
                    </form>
                </div>
                <div class="mail-subject" v-if="mailMode">
                    <input class="subject-input" v-model="subject" placeholder="Subject.." />
                </div>
                <div
                    v-if="shortcutReminder"
                    class="d-flex justify-content-start align-items-center mt-1 shortcut-tippy"
                >
                    <span class="material-icons" style="color: #efa445">lightbulb</span>
                    <strong>{{ shortcut.shortcutKey }}</strong>
                    <span class="ml-1">{{ shortcut[$i18n.locale] }}</span>
                </div>
            </b-col>
            <b-col v-if="searchVisible" class="pl-1.5 pb-3" data-v-step="20" lg="3" md="2">
                <MessageSearch :conversation-id="activeConversation._id" />
            </b-col>
            <b-col v-else-if="contentWidgetVisible" class="pl-1.5 pb-3" data-v-step="20" lg="3" md="2">
                <DefaultContensWidget :onShortcutSelected="onShortcutSelected" />
            </b-col>
            <b-col
                :lg="searchVisible || contentWidgetVisible ? 3 : 4"
                :md="searchVisible || contentWidgetVisible ? 4 : 3"
                class="pl-1.5 pb-3"
                data-v-step="21"
            >
                <UserInfoWidget />
            </b-col>
        </b-row>
        <UserChatHistoryWidget v-if="isChatHistoryOpen" />
        <a-modal id="smsModal" v-model="smsModalVisible" :footer="null" :closable="false" :header="null">
            <div class="sms-modal-cancel" @click="smsModalVisible = false">
                <span class="md-icon" style="color: #ffa800; cursor: pointer">cancel</span>
            </div>
            <div class="sms-modal-image">
                <smsAlertModal style="width: 500px; max-height: 300px" />
            </div>
            <p class="sms-modal-text">{{ $t("whatsappChannel.smsModalText") }}</p>
            <div class="sms-modal-btn">
                <button class="push-btn" @click="pushSmsChannel">
                    {{ $t("whatsappChannel.smsModalBtn") }}
                </button>
            </div>
        </a-modal>
    </div>
</template>

<script>
import "../utils/request";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import moment from "moment";
import "vue-wysiwyg/dist/vueWysiwyg.css";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue";
import UserInput from "./popup-chat/UserInput";
import getRandomShortcut from "../utils/shortcutAll";
import { capitalize, fakeConversation, getExtensionFromFileName, isMac } from "../utils/utils";
import languages from "../language/languages.json";
import Fuse from "fuse.js";
import EmojiButton from "./EmojiButton.vue";
import {
    faBan,
    faBoxOpen,
    faCopy,
    faGlobe,
    faHeadset,
    faInfo,
    faPaperPlane,
    faPowerOff,
    faShare,
    faSignOutAlt,
    faTimes,
    faUser,
    faUserSecret,
    faWindowMinimize,
} from "@fortawesome/free-solid-svg-icons";
import Info from "./svg/Info";
import Representative from "./svg/Representative";
import SendIcon from "./svg/SendIcon";
import ChatId from "./svg/ChatId";
import Operating from "./svg/Operating";
import World from "./svg/World";
import Ip from "./svg/Ip";
import User from "./svg/User";
import Mail from "./svg/Mail";
import VideoCallSvg from "./svg/VideoCallSvg";
import KeywordShortCut from "./svg/KeywordShortCut";
import ConversationColor from "./svg/ConversationColor";
import PowerOff from "./svg/PowerOff";
import ChatWindowSettings from "./svg/ChatWindowSettings";
import HideChat from "./svg/HideChat";
import Block from "./svg/Block";
import ShowIntro from "./svg/ShowIntro";
import CircleCloseButton from "./svg/CircleCloseButton";
import AssignRepresentative from "./svg/AssignRepresentative";
import ForwardRepresentative from "./svg/ForwardRepresentative";
import PrivateMessageIcon from "./svg/PrivateMessageIcon";
import MinimizeWindow from "./svg/MinimizeWindow";
import PrivateChatRepresentative from "./PrivateChatRepresentative";
import ConversationColorHelp from "./ConversationColorHelp";
import KeyboardShortcuts from "./KeyboardShortcuts";
import UserInfoWidget from "./UserInfoWidget";
import DefaultContensWidget from "./DefultContentsWidget";
import UserChatHistoryWidget from "./UserChatHistoryWidget";
import MessageList from "./popup-chat/MessageList";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, first, map } from "rxjs/operators";
import { createChatWindowTourSteps } from "../utils/chatWindowTourSteps";
import Cookies from "js-cookie";
import SYSTEM_PUBSUB, { publishMessage, systemMessageStream } from "../utils/systemPubSub";
import { toNewMessagesModel } from "../utils/chat";
import { paragraphStyleExtender } from "./util/paragraphStyleExtender";
import PrivateMessage from "./PrivateMessage";
import ForgotPassword from "../views/Pages/ForgotPassword";
import { PHONE } from "../webrtc/phoneSDK";
import VideoCallScreen from "./VideoCallScreen";
import { PHONE_UTILS } from "../utils/phone";
import { uploadAsset } from "../api/assetUpload";
import axios from "axios";
import VoiceRecorder from "./VoiceRecorder";
import mimeTypes from "./util/mimeTypes";
import Call from "./svg/Call";
import VideoCallCloseSvg from "./svg/VideoCallCloseSvg";
import OpenChat from "./svg/OpenChat";
import CONFIG from "../config/config";
import { getPersistenceData } from "../utils/persistenceStorage";
import ActionEdit from "./svg/ActionEdit";
import { generateUUID } from "../utils/session";
import { Mentionable } from "vue-mention";
import LocationShare from "./LocationShare";
import EmojiPicker from "./popup-chat/EmojiPicker.vue";
import EmojiIcon from "./popup-chat/icons/EmojiIcon.vue";
import { PERMISSION_TYPES } from "../language/permissionTypes";
import Message from "./popup-chat/Message.vue";
import MessageViewer from "./popup-chat/MessageViewer.vue";

import MessageSearch from "./MessageSearch.vue";
import SMSInput from "./popup-chat/SMSInput.vue";
import channel from "../store/modules/channel";
import smsAlertModal from "../views/Pages/Chatbot/pages/integrations/svg/smsAlertModal.vue";
import AIAssistant from "./popup-chat/AIAssistant.vue";
import MicButton from "../utils/speech/MicButton.vue";
import SpeechToTextService from "../utils/speech/SpeechToTextService";
import * as Rx from "rxjs";
import VisitorBanModal from "./VisitorBanModal.vue";

library.add(
    faUser,
    faPowerOff,
    faPaperPlane,
    faInfo,
    faWindowMinimize,
    faCopy,
    faBan,
    faTimes,
    faBoxOpen,
    faUserSecret,
    faSignOutAlt,
    faShare,
    faHeadset,
    faGlobe
);
const log = log2("ChatWindow");
log("CKEditor", { CKEditor, ClassicEditor });
export default {
    name: "ChatWindow",
    components: {
        VisitorBanModal,
        MicButton,
        AIAssistant,
        SMSInput,
        MessageViewer,
        MessageList,
        Message,
        LocationShare,
        Mentionable,
        VoiceRecorder,
        ForwardRepresentative,
        ForgotPassword,
        PrivateMessage,
        Block,
        UserChatHistoryWidget,
        DefaultContensWidget,
        UserInfoWidget,
        User,
        Mail,
        Ip,
        World,
        Operating,
        ChatId,
        SendIcon,
        Representative,
        Info,
        KeywordShortCut,
        ConversationColor,
        ConversationColorHelp,
        KeyboardShortcuts,
        CKEditor,
        UserInput,
        PowerOff,
        ChatWindowSettings,
        HideChat,
        PrivateChatRepresentative,
        AssignRepresentative,
        PrivateMessageIcon,
        MinimizeWindow,
        CircleCloseButton,
        ShowIntro,
        VideoCallScreen,
        VideoCallSvg,
        VideoCallCloseSvg,
        Call,
        OpenChat,
        ActionEdit,
        EmojiPicker,
        EmojiIcon,
        EmojiButton,
        MessageSearch,
        smsAlertModal,
    },
    component: {
        Info,
        Representative,
        SendIcon,
        ChatId,
        Operating,
        World,
    },
    data() {
        return {
            speechRecognitionActive: false,
            speechRecognitionContinousMode: false,
            speechRecognition: null,
            smsModalVisible: false,
            isPopoverVisible: false,
            uploadPercentage: 0,
            show: false,
            dragMode: false,
            languages,
            renderCount: 0,
            messageSyncRunning: false,
            tourCallbacks: {
                onPreviousStep: this.onTourPrev,
                onNextStep: this.onTourNext,
                onStart: this.onTourStart,
                onStop: this.onTourStop,
            },
            steps: createChatWindowTourSteps(this),
            keydownBinded: false,
            message: "",
            markedBannedMessage: "",
            messages: [],
            pmMessage: "",
            suggestionsCurrent: 0,
            bannedWordDetected: false,
            waitingTabShortcut: false,
            isHashShortcutMode: false,
            suggestions: [],
            typingMessage: "",
            lastChar: "",
            cancelTokens: [],
            editor: ClassicEditor,
            editorConfig: () => ({
                // The configuration of the editor.
                toolbar: ["bold", "italic", "link"],
                placeholder: this.$t("message.typeHere"),
                extraPlugins: [],
                startupFocus: true,
                removePlugins: ["MediaEmbed", "AutoMediaEmbed"],
            }),
            wysiwyg: {
                paragraphSeparator: "br",
                maxHeight: "100px",
                hideModules: {
                    justifyLeft: true,
                    justifyCenter: true,
                    justifyRight: true,
                    headings: true,
                    code: true,
                    orderedList: true,
                    unorderedList: true,
                    image: true,
                    table: true,
                    removeFormat: true,
                    separator: true,
                },
            },
            chatColorModalVisible: false,
            keyboardShortCutsModalVisible: false,
            colors: {
                header: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                launcher: {
                    bg: "#4e8cff",
                },
                messageList: {
                    bg: "#ffffff",
                },
                sentMessage: {
                    bg: "#4e8cff",
                    text: "#ffffff",
                },
                receivedMessage: {
                    bg: "#eaeaea",
                    text: "#222222",
                },
                userInput: {
                    bg: "#f4f7f9",
                    text: "#565867",
                },
            },
            wideScreen: false,
            layout: false,
            shortcut: getRandomShortcut(),
            callingSound: new Audio("./static/media/videoCallSounds/incoming-call.ogg"),
            coolingTime: Date.now(),
            pendingMessages: [],
            mentions: [
                {
                    value: "email",
                    label: "email",
                },
                {
                    value: "name",
                    label: "test",
                },
            ],
            draftMessageTimers: {},
            visitorBanned: false,
            visitor: {},
            searchVisible: false,
            newCreatedMailIdClone: "",
            mailMode: false,
            smsMode: false,
            subject: "",
            isSendBtnActive: true,
            visitorBanModal: false,
        };
    },
    beforeDestroy() {
        log("beforeDestroy", this.prevChatId);
        if (this.prevChatId) {
            try {
                const message = this.getCKInstance(this.prevChatId).ui.editor.getData("");
                log("beforeDestroy", { message, prevChatId: this.prevChatId });
                if (!!message) this.addDraftMessageDelayed(this.prevChatId, message, 0);
            } catch (e) {
                log.warn("addDraftMessageDelayed error", e);
            }
        }
        this.messages = [];
        this.cancelTokens.forEach((cancel) => cancel());
        this.callingSound.pause();
        if (!!this.loadMoreSubscriber) {
            this.loadMoreSubscriber.unsubscribe();
        }
        if (this.lastReSyncIntervalId) {
            clearInterval(this.lastReSyncIntervalId);
        }
        this.destroyCKInstance(this.prevChatId);
    },
    mounted() {
        this.newCreatedMailIdClone = _.cloneDeep(this.newCreatedMailId);
        log("ChatWindow mounted", {
            activeConversation: this.activeConversation,
            contents: this.contents.data,
        });
        if (this.activeConversation) {
            this.setupPressAndTalk();
            this.prevChatId = this.activeConversation._id;
            this.syncMessages(this.activeConversation._id, false, true);

            document.querySelector("#chatWindow").focus();

            const showChatIntroSub = systemMessageStream
                .pipe(filter((x) => x.type === "showChatIntro"))
                .subscribe((x) => {
                    this.$tours["chat-window-tour"].start();
                });
            // const enterSub = systemMessageStream.pipe(filter((x) => x.type === "chat.enterPress")).subscribe((x) => {
            //     this.sendMessage();
            // });
            // this.cancelTokens.push(() => enterSub.unsubscribe());
            this.cancelTokens.push(() => showChatIntroSub.unsubscribe());
            this.bannedWordClearTimerId = setInterval(() => {
                if (this.bannedWordDetected && Date.now() - this.lastMessageUpdateTS > 500) {
                    this.lastCheckedBannedMessage = this.markBannedWords(this.message);
                    if (!this.lastCheckedBannedMessage.includes('class="banned"')) {
                        this.bannedWordDetected = false;
                        this.message = this.lastCheckedBannedMessage;
                    }
                }
            }, 500);
            this.cancelTokens.push(() => clearInterval(this.bannedWordClearTimerId));
            // const syncMessageTimerId  = setTimeout(() => {
            //     this.syncMessages(undefined, true);
            // }, 2000);
            // this.cancelTokens.push(() => clearTimeout(syncMessageTimerId));
            this.bindDraftMessage();
            this.listenLoadMore();
            this.setupResyncMessageWorker(this.activeConversation._id);
        } else {
            log.error("active chat is not setted");
        }
        let translateSub = systemMessageStream
            .pipe(
                filter((x) => x.type === "translate_message"),
                map((x) => x.payload)
            )
            .subscribe((x) => {
                this.messages = this.messages.map((msg) => {
                    if (msg.uniqMessageId === x.uniqMessageId) {
                        msg.translates = [...msg.translates, ...x.translates];
                    }
                    return msg;
                });
            });

        let messageUpdateSub = socketMessageStream
            .pipe(filter((x) => ["message_updated", "message_deleted"].includes(x.cmd)))
            .subscribe((x) => {
                const message = x.data;
                this.messages = this.messages.map((msg) => {
                    if (msg.uniqMessageId === message.uniqMessageId) {
                        if (x.cmd === "message_updated") {
                            return message;
                        } else if (x.cmd === "message_deleted") {
                            x.isDeleted = true;
                            return _.cloneDeep(x);
                        }
                    }
                    return msg;
                });
            });
        this.cancelTokens.push(() => {
            translateSub.unsubscribe();
            messageUpdateSub.unsubscribe();
        });
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            language: (state) => state.language.language,
            user: (state) => state.auth.user,
            shortcutReminder: (state) => state.app.shortcutReminder,
            isRoot: (state) => state.auth.user.role.isRoot,
            observing: (state) => state.chat.observing,
            contents: (state) => state.content.contents,
            wallet: (state) => state.wallet.wallet,
            contentWidgetVisible: (state) => {
                const activeChat = state.chat.activeConversation;
                return (
                    state.content.contents.data.filter((x) => x.type !== "TITLE").length > 0 &&
                    activeChat &&
                    !state.chat.observing.includes(activeChat._id)
                );
            },
            activeConversation: (state) =>
                state.app.fakeMode ? state.chat.fakeActiveConversation : state.chat.activeConversation,
            draftMessages: (state) => state.chat.draftMessages,

            settings: (state) => state.setting.settings,
            videoCallFeatureEnabled: (state) => _.get(state, "setting.settings.videocallEnabled", true),
            audioCallFeatureEnabled: (state) => _.get(state, "setting.settings.audiocallEnabled", true),
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            agentVoiceRecordEnabled: (state) => _.get(state, "setting.settings.agentVoiceRecordEnabled", true),
            customerLocationEnabled: (state) => _.get(state, "setting.settings.customerLocationEnabled", true),
            site: (state) => state.site.site,
            bannedWords: (state) => state.bannedWord.bannedWords.data.filter((f) => f.isActive).map((x) => x.content),
            isChatHistoryOpen: (state) => state.chat.isChatHistoryOpen,
            CustomLoginInputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
            videoCallMode: (state) => state.webrtc.videoCallMode,
            calling: (state) => state.webrtc.calling,
            soundOnly: (state) => state.webrtc.soundOnly,
            socketConnected: (state) => state.socketStatus.socket_connected,
            chatHistory: (state) => state.chat.chatHistory,
            replyToMessage: (state) => _.get(state.chat.pinnedReplyToMessages, state.chat.activeConversation?._id),
            allMailAddresses: (state) => state.mail.allMailAddresses,
            newCreatedMailId: (state) => state.mail.newCreatedMailId,
            selectedMailThreadId: (state) => state.mail.selectedMailThreadId,
            smsChannels: (state) => state.channel.channels.filter((x) => x.type === "sms-postacell"),
            copilotEnable: (state) => {
                const channel = state.channel.channels.find((x) => x.type === "openai");
                const isActive = state.setting.settings?.chatAiCopilot || false;
                return channel && isActive;
            },
        }),
        ...mapGetters({
            getActiveConversations: "chat/getActiveConversations",
            getActiveConversationPendingMessages: "chat/getActiveConversationPendingMessages",
            getUser: "user/getUser",
            grouped: "content/getGrouped",
            isDarkMode: "user/isDarkMode",
            isCalling: "webrtc/isCalling",
            isWhatsappHealthy: "whatsapp/isHealthy",
            checkPermission: "auth/checkPermission",
        }),
        isSwitched: {
            get() {
                return this.autoTranslate.includes(this.activeConversation._id);
            },
            set(val) {
                this.setAutoTranslate({
                    id: this.activeConversation._id,
                    state: val,
                });
                if (this.autoTranslate.includes(this.activeConversation._id)) {
                    this.$message.success({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.on")}`,
                        duration: 3,
                        key: "translate",
                    });
                } else {
                    this.$message.warning({
                        content: `${this.$t("message.autoTranslate")} ${this.$t("message.off")}`,
                        duration: 3,
                        key: "translate",
                    });
                }
            },
        },

        filteredMessages() {
            return this.messages;
        },
        messageModel: {
            get() {
                return this.message;
            },
            set(value) {
                let msg = value;
                /*let msg = this.markBannedWords(value)*/
                msg = msg.replaceAll("&#8203;", "");
                msg = this.trimNewLine(msg);
                this.lastMessageUpdateTS = Date.now();
                this.message = msg;
            },
        },
    },
    methods: {
        ...mapActions({
            resetChatHistory: "chat/resetChatHistory",
            addObservation: "chat/addObservation",
            setLoadingState: "chat/setLoadingState",
            getLoadingState: "chat/getLoadingState",
            getLoadingFilePercent: "chat/getLoadingFilePercent",
            endConversation: "chat/endConversation",
            leaveConversation: "chat/leaveConversation",
            hideConversation: "chat/hideConversation",
            banConversation: "chat/banConversation",
            loadOldMessages: "chat/loadOldMessages",
            loadOldHistoryMessages: "chat/loadOldHistoryMessages",
            loadLastNMessage: "chat/loadLastNMessage",
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            setShortcutReminder: "app/setShortcutReminder",
            setVideoCallMode: "webrtc/setVideoCallMode",
            setCalling: "webrtc/setCalling",
            viewFakeConversation: "chat/viewFakeConversation",
            multiBeepConversation: "chat/multiBeepConversation",
            joinConversation: "chat/joinConversation",
            addPendingMessage: "chat/addPendingMessage",
            addDraftMessage: "chat/addDraftMessage",
            removeDraftMessage: "chat/removeDraftMessage",
            banVisitor: "visitor/banVisitor",
            unpinReplyToMessage: "chat/unpinReplyToMessage",
            createMailAddress: "mail/createMailAddress",
            getChannel: "channel/getChannel",
        }),
        ...mapMutations({
            setAutoTranslate: "chat/SET_AUTO_TRANSLATE",
        }),

        setupPressAndTalk() {
            //start recognition with (CTRL + ")
            const subject = new Rx.Subject();

            const keydownFN = (event) => {
                const isQuoteKey = event.key === '"';
                const meteKey = isMac ? event.metaKey : event.ctrlKey;
                if (meteKey && isQuoteKey) {
                    subject.next(true);
                }
            };
            const keyupFN = (event) => {
                const isQuoteKey = event.key === '"';
                const meteKey = event.key === "Meta" || event.key === "Control";
                if (isQuoteKey || meteKey) {
                    subject.next(false);
                }
            };
            document.addEventListener("keydown", keydownFN);

            document.addEventListener("keyup", keyupFN);

            subject.subscribe((isActive) => {
                if (isActive) {
                    if (!this.speechRecognitionActive) {
                        this.speechRecognitionContinousMode = true;
                        this.speechRecognitionActive = true;
                    }
                } else {
                    if (this.speechRecognitionActive) {
                        this.speechRecognitionContinousMode = false;
                        this.speechRecognitionActive = false;
                    }
                }
            });
            this.cancelTokens.push(() => {
                document.removeEventListener("keydown", keydownFN);
                document.removeEventListener("keyup", keyupFN);
                subject.unsubscribe();
            });
            //window events and is no event cancel recogniton
        },
        destroyCKInstance(id) {
            log("destroyCKInstance", { id });
            try {
                const instance = this.getCKInstance(id);
                if (instance) {
                    instance.destroy();
                    log("CKEditor destroyed");
                } else {
                    log("CKEditor not destroyed intance not found");
                }
            } catch (e) {
                console.error(e);
            }
        },
        switchMode(mode = "chat") {
            if (mode === "chat") {
                this.mailMode = false;
                this.smsMode = false;
            } else if (mode === "email") {
                this.mailMode = true;
                this.smsMode = false;
            } else if (mode === "sms") {
                if (this.smsChannels.length === 0) {
                    this.smsModalVisible = true;
                } else {
                    this.mailMode = false;
                    this.smsMode = true;
                }
            }
        },
        forwardRep() {
            this.isPopoverVisible = false;
            this.$emit("input", "forward-agent");
        },
        emailFeatureEnabled() {
            return CONFIG.EMAIL_SERVICE_ENABLED;
        },
        waitMail(requestId) {
            return socketMessageStream
                .pipe(
                    first((x) => x.cmd === "email_send_successful" && x.data.requestId === requestId),
                    map((x) => x.data.email)
                )
                .toPromise();
        },
        pushSmsChannel() {
            this.$router.push("/integrations/sms/postacell");
        },

        async sendSMS({ text, channelId, senderId, number }) {
            const payload = {
                siteId: this.site._id,
                channelId,
                channelType: "sms-postacell",
                number,
                message: text,
                senderId,
                title: null,
                customMessageId: generateUUID(),
            };
            sendSocketMessage("sms send-single", payload);
            const activeChat = this.activeConversation;
            let data = {
                conversationId: activeChat._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                messageType: "user-sms",
                message: text,
                channelId: channelId,
                whatsappNumber: _.get(activeChat, "whatsappNumber", null),
                instagramUserId: _.get(activeChat, "instagramUserId", null),
                messengerUserId: _.get(activeChat, "messengerUserId", null),
                telegramUserId: _.get(activeChat, "telegramUserId", null),
                uniqMessageId: generateUUID(),
            };
            this.addPendingMessage(data);
            sendSocketMessage("user sms", data);
        },
        async sendMail() {
            if (this.message.length === 0) {
                this.$message.error("Mesaj alanı boş geçilemez");
            } else if (this.subject.length === 0) {
                this.$message.error("Subject alanı boş geçilemez");
            } else if (!this.activeConversation.visitor.email || this.activeConversation.visitor.email.length === 0) {
                this.$message.error("Kullanıcı mail adresi bulunmamaktadır");
            } else {
                const activeChat = this.activeConversation;
                this.$message.warning("Mail gönderiliyor...");
                this.createMailAddress({
                    siteId: this.site._id,
                    mail: activeChat.visitor.email,
                    type: "visitor",
                    visitorId: activeChat.visitor._id,
                });
                const userMailAddress = this.allMailAddresses.filter(
                    (x) => !!x.user && x.user._id === this.user._id && x.idDefault === true
                );
                const regex = /(<([^>]+)>)/gi;
                const textMessage = this.message.replace(regex, "");
                const data = {
                    ref_visitor_id: activeChat.visitor._id,
                    to_list: [activeChat.visitor.email],
                    sender: userMailAddress[0].mail,
                    ref_user_ids: [this.user._id],
                    senderName: this.user.name,
                    body_html: this.message,
                    body_text: textMessage,
                    subject: this.subject,
                    attachments: [],
                    fileNames: [],
                    files: [],
                };
                const requestId = generateUUID();
                const emailPromise = this.waitMail(requestId);
                this.isSendBtnActive = false;
                sendSocketMessage("email send", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    requestId,
                    data,
                });
                const email = await emailPromise;
                this.isSendBtnActive = true;
                let payload = {
                    conversationId: activeChat._id,
                    mail: email,
                    username: this.user.name,
                    userId: this.user._id,
                    siteId: this.site._id,
                    messageType: "user-email",
                    message: "",
                    channelId: _.get(activeChat, "channel", null),
                    whatsappNumber: _.get(activeChat, "whatsappNumber", null),
                    instagramUserId: _.get(activeChat, "instagramUserId", null),
                    messengerUserId: _.get(activeChat, "messengerUserId", null),
                    telegramUserId: _.get(activeChat, "telegramUserId", null),
                    uniqMessageId: generateUUID(),
                };
                this.addPendingMessage(payload);
                sendSocketMessage("user mail", payload);
                this.message = "";
                this.subject = "";
            }
        },
        hasUserMail() {
            if (this.activeConversation.email.includes("@c.us")) {
                return false;
            } else {
                return true;
            }
        },
        getChannelNumber() {
            return "+" + this.activeConversation.email.split("@")[0];
        },
        removeReplyTo(conversationId) {
            this.unpinReplyToMessage(conversationId);
        },
        addEmojiToMessage(emoji) {
            const regex = /(<([^>]+)>)/gi;
            const newMessage = this.messageModel.replaceAll(`<p>&nbsp;</p>`, "").replace(regex, "");
            this.messageModel = newMessage + emoji;
        },
        isTranslationRequired() {
            const isActive = this.autoTranslate.includes(this.activeConversation._id);
            const preferredLang = _.get(this.activeConversation, "visitor.preferredLang");
            if (!preferredLang) return false;
            const langIsDifferent = this.user.lang !== preferredLang;
            const isWebChat = !(
                this.activeConversation.whatsappNumber ||
                this.activeConversation.instagramUserId ||
                this.activeConversation.messengerUserId ||
                this.activeConversation.telegramUserId
            );
            const isEnabledFromSettings = this.settings.autoTranslate;
            return isEnabledFromSettings && !isActive && langIsDifferent && isWebChat && !this.messageSyncRunning;
        },
        isInputAllowed() {
            const isWhatsappChat = !!this.activeConversation.whatsappNumber;
            if (isWhatsappChat) {
                return true || this.isWhatsappHealthy;
            }
            return true;
        },

        async uploadFile(file, uploadFile) {
            const fileType = getExtensionFromFileName(file.name);

            let path = `${this.site._id}/file/${this.activeConversation._id}`;

            let { data } = await uploadAsset({
                path,
                mime: file.type,
                name: file.name,
                ext: mimeTypes[file.type] || fileType || "supsis",
                platform: this.activeConversation.ref,
            });
            if (file.size <= CONFIG.MAX_FILE_UPLOAD_SIZE * 1024 * 1024) {
                const conversationId = this.activeConversation._id;
                const socketFeedbackMessage = {
                    conversationId: conversationId,
                    userId: this.user._id,
                    username: this.user.name,
                    siteId: this.site._id,
                    asset: data.asset,
                    channelId: _.get(this.activeConversation, "channel", null),
                    whatsappNumber: _.get(this.activeConversation, "whatsappNumber", null),
                    instagramUserId: _.get(this.activeConversation, "instagramUserId", null),
                    messengerUserId: _.get(this.activeConversation, "messengerUserId", null),
                    telegramUserId: _.get(this.activeConversation, "telegramUserId", null),
                    replyToMessage: _.get(this.replyToMessage, "raw"),
                };
                await axios({
                    method: data.method,
                    url: data.url,
                    data: file,
                    headers: {
                        "Content-Type": file.type,
                    },
                    onUploadProgress: await function (progressEvent) {
                        const percent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                        uploadFile.percent = percent;
                        this.setLoadingState(uploadFile);

                        this.getLoadingFilePercent(conversationId).then((percent) => {
                            this.uploadPercentage = percent;
                            this.show = !!percent;
                        });
                    }.bind(this),
                })
                    .then((result) => {
                        this.show = false;
                        uploadFile.finished = true;
                        this.uploadPercentage = 0;
                        this.setLoadingState(uploadFile);
                        this.getLoadingState(uploadFile.fileNumber).then((result) => {
                            sendSocketMessage("user media message", socketFeedbackMessage);
                        });
                        this.removeReplyTo(this.activeConversation._id);
                    })
                    .catch((err) => {
                        this.show = false;
                        uploadFile.failed = true;
                        this.setLoadingState(uploadFile);
                    });
            } else {
                log.warn("input is not suitable for send");
                this.$toasted.error(this.$t("message.assetOversizeMB", { mb: CONFIG.MAX_FILE_UPLOAD_SIZE }), {
                    duration: 3000,
                    position: "bottom-center",
                });
            }
        },
        async handleTPaste(event) {
            event.preventDefault();
            if (!this.agentFileSendEnabled) {
                return this.$message.error(this.$t("message.dndBlocked"));
            }
            log("handleTPaste", event);
            const files = _.get(event, "clipboardData.files");
            if (files && files.length > 0) {
                const chat_id = this.activeConversation._id;
                for (let i = 0; i < files.length; i++) {
                    let uploadFile = {
                        chat_id: chat_id,
                        fileNumber: Math.random(),
                        finished: false,
                        percent: 0,
                        failed: false,
                    };
                    await this.uploadFile(files[i], uploadFile);
                    event.stopPropagation();
                }
                return true;
            }
        },

        dragEnter(e) {
            this.dragMode = true;
        },
        dragLeave() {
            this.dragMode = false;
        },
        async dropBlock(ev) {
            this.dragMode = false;
            ev.preventDefault();
            if (!this.agentFileSendEnabled) {
                return this.$message.error(this.$t("message.dndBlocked"));
            }
            const files = _.get(ev, "dataTransfer.files");
            if (files && files.length > 0) {
                const chat_id = this.activeConversation._id;
                for (let i = 0; i < files.length; i++) {
                    let uploadFile = {
                        chat_id: chat_id,
                        fileNumber: Math.random(),
                        finished: false,
                        percent: 0,
                        failed: false,
                    };
                    await this.uploadFile(files[i], uploadFile);
                    ev.stopPropagation();
                }
            }
        },
        dragOver(ev) {
            ev.preventDefault();
        },

        bindDraftMessage(conversationId) {
            const draftMessage = this.draftMessages[conversationId || this.activeConversation._id];
            if (draftMessage) {
                setTimeout(() => {
                    log("Draft message found", draftMessage, this.getCKInstance());
                    this.message = draftMessage;
                    try {
                        this.moveCursorToEnd();
                    } catch (e) {
                        log.error(e);
                    }
                }, 100);
            }
            return draftMessage;
        },
        getVisitorAvatarSrc() {
            if (this.activeConversation.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${this.activeConversation.whatsappNumber}&siteId=${
                    this.site._id
                }&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            } else if (this.activeConversation.instagramUserId) {
                return _.get(this.activeConversation, "visitor.instagramInfo.profile_pic");
                // return `https://static.supsis.live/${this.site._id}/instagram/avatar/${this.activeConversation.instagramUserId}.jpg`;
            } else if (this.activeConversation.messengerUserId) {
                return _.get(this.activeConversation, "visitor.messengerInfo.profile_pic");
                // return `https://static.supsis.live/${this.site._id}/instagram/avatar/${this.activeConversation.instagramUserId}.jpg`;
            }
            return undefined;
        },
        capitalize,
        joinTheChat(conversation, minimized = false, autoAssign = false) {
            if (this.fakeMode) {
                this.viewFakeConversation(fakeConversation());
                return;
            }
            this.multiBeepConversation([conversation._id]);
            this.joinConversation({
                conversation,
                observe: false,
                minimized: minimized,
                autoAssign: autoAssign,
                sendToSocket: true,
            });
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        handleVisitorNameInputKeyUp(event) {
            const el = this.$refs.visitorNameInput;
            let fullname = el.textContent.toString();
            const input = String.fromCharCode(event.keyCode);
            const isValidInput = /[a-zA-Z0-9-_ ]/.test(input);
            const isEmpty = fullname.trim().length === 0;
            log("fullname keyup", { input, isValidInput, isEmpty, fullname }, event);
            if (isEmpty && isValidInput) {
                el.textContent = input;
                setTimeout(() => {
                    const end = el.textContent.length;
                    el.setSelectionRange(end, end);
                    el.focus();
                }, 100);
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        async selectFile() {
            let fileEl = document.createElement("input");
            fileEl.setAttribute("type", "file");
            fileEl.onchange = async () => {
                let file = fileEl.files[0];
                let uploadFile = {
                    chat_id: this.activeConversation._id,
                    fileNumber: Math.random(),
                    finished: false,
                    percent: 0,
                    failed: false,
                };
                await this.uploadFile(file, uploadFile);
            };
            fileEl.click();
        },
        getEmail() {
            return this.getCustomLoginInputField(this.activeConversation.customLoginInfos, "email", undefined);
        },
        getFullname() {
            const result = this.getCustomLoginInputField(
                this.activeConversation.customLoginInfos,
                "fullname",
                undefined
            );
            log("getFullname", result);
            return result;
        },
        getCustomLoginInputField(customLoginObject = [], requestedField, defaultValue) {
            let inputValues = customLoginObject
                .filter((x) => this.CustomLoginInputs[x.input])
                .map((v) => ({
                    value: v.value,
                    name: this.CustomLoginInputs[v.input].type,
                }));
            const input = inputValues.find((x) => x.name === requestedField);
            return _.get(input, "value", defaultValue);
        },
        onEditorReady(editor) {
            log.debug("onEditorReady");
            window.ckeditor = editor;

            paragraphStyleExtender(editor);
            editor.editing.view.focus();
            this.ckeditor = editor;

            setTimeout(() => {
                this.keydownBinded = false;
                this.bindKeydownCKEditor();
                this.moveCursorToEnd();
            }, 100);
        },
        onTourPrev(currentStep) {
            log.debug("tour prev", currentStep);
        },
        onTourNext(currentStep) {
            log.debug(
                "tour next",
                currentStep,
                this.$tours["chat-window-tour"].steps[currentStep + 1].target,
                this.$tours["chat-window-tour"]
            );
            /*if(this.$tours["chat-window-tour"].steps[currentStep+1].target === '[data-v-step="hideAction"]'){
$('#ellipsisButton').trigger("click")
this.$forceUpdate()
}*/
        },
        onTourStart() {
            log.debug("tour start");
            if (
                this.activeConversation.status === "destroyed" ||
                this.observing.includes(this.activeConversation._id)
            ) {
                _.remove(this.steps, function (n) {
                    return (
                        n.target === '[data-v-step="closeButton2"]' ||
                        n.target === '[data-v-step="preparedContentWidget"]'
                    );
                });
                log("Unnecessary tour steps removed for observing mode and destroyed conversations", this.steps);
            }
        },
        onTourStop() {
            log.debug("tour stop");
            Cookies.set("chat_tour_viewed", "true");
        },

        showTour() {
            log("this.$tours['chat-window-tour']", this.$tours["chat-window-tour"]);
            this.$tours["chat-window-tour"].start();
            publishMessage("showChatIntro", {});
        },
        skipTour() {
            Cookies.set("chat_tour_viewed", "true");
        },
        getParticipants() {
            const supportUsers = this.messages
                .filter((x) => x.messageType === "user" || x.messageType === "private")
                .map((x) => ({
                    id: x.userId,
                    name: x.username,
                }));

            return _.uniqBy(supportUsers, (q) => q.name);
        },
        getMessages() {
            let pendingMessages = this.getActiveConversationPendingMessages;
            let allMessages = toNewMessagesModel(this.messages.concat(pendingMessages), this);
            log("getMessages", { allMessages, old: this.messages });
            if (this.typingMessage) {
                allMessages = [
                    ...allMessages,
                    {
                        type: "typing",
                        data: {
                            text: this.typingMessage,
                        },
                    },
                ];
            }
            let extendedMessageList = [];
            const isUserMessage = (messageType) => ["user", "user-media", "private"].includes(messageType);
            let isHumanMessage = (messageType) =>
                [
                    "user",
                    "user-media",
                    "user-location",
                    "visitor",
                    "visitor-location",
                    "visitor-media",
                    "whatsapp-user",
                    "whatsapp-user-media",
                    "whatsapp-user-location",
                    "instagram-user",
                    "instagram-user-media",
                    "instagram-user-location",
                ].includes(messageType);
            let lastHumanMessage = null;
            for (let i = 0; i < allMessages.length; i++) {
                const current = allMessages[i];
                const createdAt = new Date(_.get(current, "raw.createdDate", null)).getTime();
                const messageType = _.get(current, "raw.messageType", null);
                extendedMessageList.push(current);
                if (isHumanMessage(messageType)) {
                    lastHumanMessage = current;
                    log("lastHumanMessage", lastHumanMessage);
                }
            }
            if (this.activeConversation.inactive) {
                const startTS = moment(
                    _.get(lastHumanMessage, "raw.createdDate", this.activeConversation.createdDate)
                ).valueOf();
                const tookTimeInSeconds = moment().diff(moment(startTS), "seconds");
                const thresholdAsSeconds = (this.settings.inactiveThresholdTime || 5) * 60;
                const isIdle = tookTimeInSeconds >= thresholdAsSeconds * 0.95;
                log("isIdle", { tookTimeInSeconds, thresholdAsSeconds, isIdle, startTS });
                if (isIdle) {
                    extendedMessageList.push({
                        type: "idle-time",
                        data: {
                            startTimestamp: startTS,
                            isLive: true,
                        },
                    });
                }
            }
            log("extendedMessageList", extendedMessageList);
            return _.orderBy(extendedMessageList, ["raw.createdDate"], ["asc"]);
        },
        toggleChatColorModal(visible) {
            log("toggleChatColorModal called", visible);
            this.chatColorModalVisible = visible;
        },
        toggleKeyboardShotcutModal(visible) {
            log("toggleKeyboardShotcutModal called", visible);
            this.keyboardShortCutsModalVisible = visible;
        },

        ban() {
            this.visitorBanModal = true;
            /*this.$dialog
                .confirm(this.$t("message.blockUserConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.banVisitor(this.activeConversation.visitor._id);
                    this.banConversation(this.activeConversation._id);
                    this.visitorBanned = true;
                });*/
        },
        pm() {
            this.isPopoverVisible = false;
            this.setPrivateMessageConversation(this.activeConversation._id);
        },
        leave() {
            this.$dialog
                .confirm(this.$t("message.leaveChartConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    const isValid =
                        this.activeConversation.status === "destroyed" ||
                        this.observing.includes(this.activeConversation._id) ||
                        this.activeConversation.users.filter((u) => u !== null).length === 1;
                    if (isValid) {
                        this.leaveConversation(this.activeConversation._id);
                    } else {
                        this.$toasted.error(this.$t("message.onlyOneRep"), {
                            duration: 3000,
                        });
                    }
                });
        },
        end() {
            this.$dialog
                .confirm(this.$t("message.endChatConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.stopVC();
                    this.endConversation(this.activeConversation._id);
                });
        },
        hide() {
            this.hideConversation();
        },
        call(soundOnly = false, screenOnly = false) {
            if (this.calling) {
                log.warn("video call request is not allowed , when another call request waiting");
                return;
            }
            log("call running");
            let config = {
                userId: this.user._id,
                secure: true,
                ssl: true,
                autocam: true,
            };
            if (!screenOnly) {
                let media = {
                    video: soundOnly ? false : { facingMode: "user" },
                    audio: true,
                };
                config = { media, ...config };
            }
            const phone = PHONE_UTILS.getPhone() || PHONE(config);
            phone.configUpdate(config);
            log("call running", { phone, soundOnly });

            PHONE_UTILS.setPhoneInstance(phone);
            phone.startcamera();
            const c = this.activeConversation;
            const visitorId = typeof c.visitor === "object" ? c.visitor._id : c.visitor;
            this.setVideoCallMode({
                videoCallMode: true,
                conversationId: c._id,
                visitorId,
                soundOnly,
            });
            this.setCalling(true);
            sendSocketMessage("user video-call-request", {
                siteId: this.site._id,
                userId: this.user._id,
                visitorId: this.activeConversation.visitor,
                conversationId: this.activeConversation._id,
                soundOnly,
            });
        },
        sendTypingInfo: _.debounce(function () {
            if (this.message.length) {
                if (!!this.activeConversation)
                    sendSocketMessage("user typing message", {
                        conversationId: this.activeConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: "rep-typing",
                        siteId: this.site._id,
                        channelId: _.get(this.activeConversation, "channel", null),
                        whatsappNumber: _.get(this.activeConversation, "whatsappNumber", null),
                        instagramUserId: _.get(this.activeConversation, "instagramUserId", null),
                        messengerUserId: _.get(this.activeConversation, "messengerUserId", null),
                        telegramUserId: _.get(this.activeConversation, "telegramUserId", null),
                    });
            }
        }, 500),
        async sendMessage(message) {
            if (this.mailMode) {
                if (!this.isSendBtnActive) {
                    return false;
                } else {
                    this.sendMail();
                }
            } else {
                if (!this.isInputAllowed()) {
                    const message = {
                        en: "You are not allowed to send message. Please check whatsapp integration",
                        tr: "Bu mesajı gönderemezseniz whatsapp entegrasyonunu kontrol edin",
                        ru: "Вы не можете отправить сообщение. Пожалуйста, проверьте интеграцию whatsapp",
                        de: "Wenn Sie diese Nachricht nicht senden können, überprüfen Sie die WhatsApp-Integration",
                        fr: "Si vous ne pouvez pas envoyer ce message, vérifiez l'intégration WhatsApp",
                        es: "Si no puede enviar este mensaje, verifique la integración de WhatsApp",
                        ar: "إذا لم تتمكن من إرسال هذه الرسالة ، فتحقق من تكامل whatsapp",
                        it: "Se non puoi inviare questo messaggio controlla l'integrazione di whatsapp",
                    };
                    this.$toasted.error(message[this.$i18n.locale], {
                        duration: 2000,
                        position: "bottom-center",
                    });
                    return;
                }
                message = (typeof message === "string" && message) || this.message;
                log("sendMessage before", {
                    pm: this.pmMessage.trim(),
                    message,
                });

                //enter pm setTen önce çalışabiliyor
                const regex = /(<([^>]+)>)/gi;

                if (_.take(message.replaceAll(`<p>&nbsp;</p>`, "").replace(regex, ""), 10).join("").startsWith("pm:")) {
                    this.pmMessage = this.pmMessage = message.trim().replace("pm:", "");
                }

                if (this.pmMessage.trim().length) {
                    sendSocketMessage("user private-message", {
                        conversationId: this.activeConversation._id,
                        userId: this.user._id,
                        username: this.user.name,
                        message: this.pmMessage,
                        siteId: this.site._id,
                        replyToMessage: _.get(this.replyToMessage, "raw"),
                    });
                    this.removeReplyTo(this.activeConversation._id);
                    this.pmMessage = "";
                    this.message = "";
                    setTimeout(() => {
                        this.message = "";
                        try {
                            //this.getCKInstance().ui.editor.setData("");
                        } catch (e) {
                            log.error(e);
                        }
                    }, 50);
                } else {
                    if (message.trim().length) {
                        let clearedMessage = message
                            .trim()
                            .replace(/↵/g, "\n")
                            .replace(/^(\s+<br( \/)?>)*|(<br( \/)?>\s)*$/gm, "")
                            .replace(/<a (.*)>/g, '<a target="_blank" $1>');
                        let bannedMarkedMessage = this.markBannedWords(clearedMessage);
                        log("sendMessage bannedMarkedMessage", {
                            bannedMarkedMessage,
                            isBanned: bannedMarkedMessage.includes("class='banned'"),
                            bannedWordDetected: this.bannedWordDetected,
                        });
                        const isBannedContain =
                            bannedMarkedMessage.includes("class='banned'") ||
                            bannedMarkedMessage.includes('class="banned"');
                        if (isBannedContain) {
                            this.message = bannedMarkedMessage;
                            log.warn("banned message detected", bannedMarkedMessage);
                            if (!this.bannedWordDetected) {
                                const message = {
                                    en: "Banned word detected, please correct message and send",
                                    tr: "Yasaklı kelime algılandı, lütfen mesajı düzeltin ve gönderin",
                                    ru: "Обнаружено запрещенное слово, пожалуйста, исправьте сообщение и отправьте",
                                    de: "Gesperrtes Wort erkannt, bitte Nachricht korrigieren und senden",
                                    fr: "Mot interdit détecté, veuillez corriger le message et envoyer",
                                    es: "Palabra prohibida detectada, corrija el mensaje y envíelo",
                                    ar: "تم الكشف عن كلمة محظورة ، يرجى تصحيح الرسالة وإرسالها",
                                    it: "Rilevata parola vietata, correggere il messaggio e inviarlo",
                                };
                                this.$toasted.error(message[this.$i18n.locale], {
                                    duration: 2000,
                                    position: "bottom-center",
                                });
                            }
                            this.bannedWordDetected = true;
                        } else {
                            const channelId = _.get(this.activeConversation, "channel", null);
                            if (channelId) {
                                const channel = await this.getChannel(channelId);
                                if (
                                    channel &&
                                    channel.type === "whatsapp-business-api" &&
                                    this.wallet.whatsapp.amount < -100
                                    //TODO: Site ayarlarından çekilecek
                                ) {
                                    this.$notification.error({
                                        message: "Error",
                                        description: "Whatsapp Hesap Bakiyeniz Yetersiz.",
                                    });
                                    return;
                                }
                            }
                            if (this.bannedWordDetected) {
                                this.bannedWordDetected = false;
                                this.message = bannedMarkedMessage;
                                this.sendMessage();
                                return;
                            }
                            log("clearedMessage", clearedMessage);
                            for (const m of ["@name", "@email"]) {
                                let val =
                                    m === "@name" ? this.activeConversation.fullname : this.activeConversation.email;
                                clearedMessage = clearedMessage.replace(new RegExp(m, "g"), val);
                            }
                            let payload = {
                                conversationId: this.activeConversation._id,
                                userId: this.user._id,
                                username: this.user.name,
                                message: clearedMessage,
                                siteId: this.site._id,
                                channelId: _.get(this.activeConversation, "channel", null),
                                whatsappNumber: _.get(this.activeConversation, "whatsappNumber", null),
                                instagramUserId: _.get(this.activeConversation, "instagramUserId", null),
                                messengerUserId: _.get(this.activeConversation, "messengerUserId", null),
                                telegramUserId: _.get(this.activeConversation, "telegramUserId", null),
                                uniqMessageId: generateUUID(),
                                replyToMessage: _.get(this.replyToMessage, "raw"),
                            };
                            sendSocketMessage("user message", payload);
                            this.removeReplyTo(this.activeConversation._id);
                            this.addPendingMessage(payload);
                            this.pmMessage = "";
                            this.message = "";
                            this.removeDraftMessage(this.activeConversation._id);
                            setTimeout(() => {
                                this.message = "";
                                try {
                                    //this.getCKInstance().ui.editor.setData("");
                                } catch (e) {
                                    log.error(e);
                                }
                            }, 50);
                        }
                    }
                }
            }

            if (this.speechRecognitionActive) {
                this.speechRecognitionActive = false;
            }
        },
        async sendVoiceMessage(blob) {
            let path = `${this.site._id}/voice/${this.activeConversation._id}`;
            const mimeType = "audio/mpeg";
            const ext = "mp3";
            let { data } = await uploadAsset({ path, mime: mimeType, ext, platform: this.activeConversation.ref });
            await axios({
                method: data.method,
                url: data.url,
                data: blob,
                // headers: { "Content-Type": "application/octet-stream" },
                headers: { "Content-Type": mimeType },
            });
            sendSocketMessage("user media message", {
                conversationId: this.activeConversation._id,
                userId: this.user._id,
                username: this.user.name,
                siteId: this.site._id,
                asset: data.asset,
                channelId: _.get(this.activeConversation, "channel", null),
                whatsappNumber: _.get(this.activeConversation, "whatsappNumber", null),
                instagramUserId: _.get(this.activeConversation, "instagramUserId", null),
                messengerUserId: _.get(this.activeConversation, "messengerUserId", null),
                telegramUserId: _.get(this.activeConversation, "telegramUserId", null),
                replyToMessage: _.get(this.replyToMessage, "raw"),
            });
            this.removeReplyTo(this.activeConversation._id);
            log("sendVoiceMessage", { blob, data });
        },
        enter(e) {
            log("enter", e);
        },
        trimNewLine(msg = "") {
            msg = msg.trim();
            while (msg.startsWith("<p>&nbsp;</p>")) {
                msg = msg.replace("<p>&nbsp;</p>", "");
            }
            while (msg.startsWith('<p><br data-cke-filler="true"></p>')) {
                msg = msg.replace('<p><br data-cke-filler="true"></p>', "");
            }
            while (msg.endsWith("<p>&nbsp;</p>")) {
                msg = msg.substring(0, msg.lastIndexOf("<p>&nbsp;</p>"));
            }
            return msg;
        },
        handleSubmit(event, data) {
            if ("Enter" === data.domEvent.key && !data.domEvent.shiftKey) {
                log("handleSubmit", {
                    waitingTabShortcut: this.waitingTabShortcut,
                    isHashShortcutMode: this.isHashShortcutMode,
                    isEnter: "Enter" === data.domEvent.key,
                    message: this.getCKInstance().ui.editor.getData(""),
                });
                data.domEvent.preventDefault();

                if (this.waitingTabShortcut || this.isHashShortcutMode) {
                    let msg = _.get(this.suggestions, [
                        this.suggestionsCurrent,
                        "languageContent",
                        this.$i18n.locale,
                        "content",
                    ]);
                    log("handleSubmit waitingTabShortcut state", {
                        suggestions: this.suggestions,
                        suggestionsCurrent: this.suggestionsCurrent,
                        msg,
                    });
                    if (!msg) {
                        return;
                    }
                    if (this.isHashShortcutMode) msg = this.message.replace(this.getHashWord(this.message), msg);
                    this.message = msg;
                    try {
                        this.getCKInstance().ui.editor.setData(msg);
                    } catch (e) {
                        log.error(e);
                    }
                    this.moveCursorToEnd();
                    if (this.isHashShortcutMode && false) {
                        this.$nextTick(() => this.sendMessage(msg));
                        this.isHashShortcutMode = false;
                    }
                    this.blurSCpanel();
                } else {
                    log("handleSubmit", this.getCKInstance().ui.editor.getData(""));

                    this.message = this.getCKInstance().ui.editor.getData("");
                    this.$nextTick(() => this.sendMessage(this.message));
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                    this.isHashShortcutMode = false;
                }
                try {
                    event.stop();
                } catch (err) {
                    log.error(err);
                }
                return false;
            }
        },
        catchShortcut(e) {
            let shortcut = false;
            let getMessage = () => {
                let elem = document.getElementsByClassName("ck-content")[0];
                let message = elem ? elem.innerText : "";
                message = _.flatten(
                    message
                        .trim()
                        .split(" ")
                        .map((x) => x.split("\n"))
                )
                    .map((x) => x.trim())
                    .filter((x) => x.length > 0)
                    .join(" ");
                var temp = document.createElement("div");
                temp.innerHTML = message;
                message = temp.textContent || temp.innerText || "";
                return message;
            };

            const isHashMode = (e.key === "#" && this.message === "") || getMessage().startsWith("#");
            log("catchShortcut", { key: e.key, message: getMessage(), isHashMode, val: this.message });
            // if (this.isHashShortcutMode !== isHashMode) this.isHashShortcutMode = isHashMode;
            if (isHashMode && e.key.toLowerCase() !== "arrowup" && e.key.toLowerCase() !== "arrowdown") {
                const message = getMessage();

                let searchTxt = message.replaceAll("#", "") + e.key;
                searchTxt = message.replaceAll("#", "");
                const searchedList = this.contents.data.filter((q) => q.type === "HASH");
                let fuzzySearch = new Fuse(searchedList, { keys: ["hash"] });
                log("fuzzySearch", { searchTxt, searchedList, fuzzySearch });
                this.suggestions =
                    searchTxt.length > 0 ? fuzzySearch.search(searchTxt).map((x) => x.item) : searchedList;
                this.suggestionsCurrent = 0;
                shortcut = document.querySelector(`div.tab[data-hash="${message.toLowerCase()}"]`);
                log("catchShortcut filtering", {
                    contents: this.contents.data,
                    message,
                    res: this.suggestions,
                    shortcut,
                });
            } else if (
                this.waitingTabShortcut &&
                e.key.toLowerCase() !== "arrowup" &&
                e.key.toLowerCase() !== "arrowdown"
            ) {
                shortcut = document.querySelector(
                    `div.tab[data-letter="${this.waitingTabShortcut}"][data-number="${e.key.toLowerCase()}"]`
                );

                if (!shortcut) {
                    log("waitingTabShortcut false 1", {
                        shortcut,
                        key: e.key,
                        qSelector: document.querySelector(
                            `div.tab[data-letter="${this.waitingTabShortcut}"][data-number="${e.key.toLowerCase()}"]`
                        ),
                    });
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                }
            } else {
                if (e.key && (isMac ? e.ctrlKey || e.metaKey : e.altKey)) {
                    shortcut = document.querySelector(`div.alt[data-letter="${e.key}"]`);
                } else if (e.key.toLowerCase() === "tab") {
                    const elem = document.getElementsByClassName("ck-content")[0];
                    const message = elem ? elem.innerText : "";

                    const allWords = _.flatten(
                        message
                            .trim()
                            .split(" ")
                            .map((x) => x.split("\n"))
                    );
                    const lastWord = _.last(allWords).toLowerCase();
                    const matchedItems = document.querySelector(`div.tab[data-letter="${lastWord}"]`);
                    log.debug("tab matchedItems", {
                        matchedItems,
                        message: this.message,
                        allWords,
                        lastWord,
                    });
                    if (matchedItems) {
                        this.waitingTabShortcut = lastWord;
                        this.suggestions = this.contents.data.filter(
                            (w) => (w.letter || "").toLowerCase() === lastWord
                        );
                    } else {
                        log("waitingTabShortcut false 2");
                        this.lastChar = "";
                        this.waitingTabShortcut = false;
                        this.suggestionsCurrent = 0;
                    }
                } else if (e.key.toLowerCase() === "arrowup" && (this.waitingTabShortcut || this.isHashShortcutMode)) {
                    log("waitingTabShortcut false 3 before", {
                        suggestionsCurrent: this.suggestionsCurrent,
                    });
                    const oldIndex = this.suggestionsCurrent;
                    if (this.suggestionsCurrent > 0) {
                        this.suggestionsCurrent--;
                        this.$refs.toolbarChat.scrollTop = this.suggestionsCurrent * 25;
                    }

                    if (this.waitingTabShortcut) {
                        this.message =
                            this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content;
                        this.moveCursorToEnd();
                    }
                    log("waitingTabShortcut false 3", {
                        suggestionsCurrent: this.suggestionsCurrent,
                    });
                    if (oldIndex === 0) {
                        this.waitingTabShortcut = false;
                        this.suggestionsCurrent = 0;
                    }
                } else if (
                    e.key.toLowerCase() === "arrowdown" &&
                    (this.waitingTabShortcut || this.isHashShortcutMode)
                ) {
                    if (this.suggestions.length - 1 > this.suggestionsCurrent) {
                        this.suggestionsCurrent++;
                        this.$refs.toolbarChat.scrollTop = this.suggestionsCurrent * 25;
                    }

                    if (this.waitingTabShortcut) {
                        this.message =
                            this.suggestions[this.suggestionsCurrent].languageContent[this.$i18n.locale].content;
                        this.moveCursorToEnd();
                    }
                }
            }

            if (shortcut) {
                log("catchShortcut found", { shortcut });
                e.preventDefault();
                const id = shortcut.getAttribute("data-id");
                const content = this.contents.data.find((x) => x._id === id);
                if (content) {
                    this.message = content.languageContent[this.$i18n.locale].content;
                    log("waitingTabShortcut false 4");
                    this.waitingTabShortcut = false;
                    this.suggestionsCurrent = 0;
                    this.moveCursorToEnd();
                }

                /*const editorEl =document.getElementById('frmChat');*/

                return false;
            } else {
                this.lastChar = e.key;
                this.focusCkEditor();
            }
        },
        moveCursorToEnd() {
            setTimeout(() => {
                if (this.ckeditor) {
                    this.ckeditor.model.change((writer) => {
                        writer.setSelection(writer.createPositionAt(this.ckeditor.model.document.getRoot(), "end"));
                    });
                }
            }, 100);
        },
        focusCkEditor() {
            log("focusCkEditor");
            setTimeout(
                function () {
                    if (this.activeConversation) {
                        const editorEl = document.getElementsByClassName("ck-editor__editable")[0];
                        if (editorEl) editorEl.focus();
                    }
                }.bind(this),
                10
            );
        },
        blurSCpanel() {
            log("blurSCpanel");
            // this.waitingTabShortcut = false;
            this.suggestionsCurrent = 0;
        },
        onShortcutSelected(str) {
            log("onShortcutSelected called", str);
            this.message = str;
            this.waitingTabShortcut = false;
            this.suggestionsCurrent = 0;
            this.focusCkEditor();
            this.moveCursorToEnd();
        },
        addMessage(data) {
            log("addMessage", data);
            let items = [...this.messages, data];
            items = _.orderBy(
                _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                ["createdDate"],
                ["asc"]
            );
            this.messages = items;
        },
        addMultiMessage(messages) {
            let items = [...this.messages, ...messages];
            items = _.orderBy(
                _.uniqBy(items, (q) => q.uniqMessageId || q._id),
                ["createdDate"],
                ["asc"]
            );
            this.messages = items;
        },
        closeButton() {
            if (this.activeConversation.users.length === 1 && !this.observing.includes(this.activeConversation._id)) {
                this.end();
            } else {
                this.leaveConversation(this.activeConversation._id);
            }
        },
        stopVC() {
            log("cancelVideoCall", this.dialingVisitorId);
            if (this.dialingVisitorId && this.dialingConversationId) {
                const phone = PHONE_UTILS.getPhone();
                sendSocketMessage("user video-call-close", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    visitorId: this.dialingVisitorId,
                    conversationId: this.dialingConversationId,
                    isSoundOnly: this.soundOnly,
                });

                if (this.videoCallMode) {
                    phone.camera.stop();
                    phone.hangup(this.dialingVisitorId);
                }
                this.setVideoCallMode({
                    videoCallMode: false,
                    conversationId: null,
                    visitorId: null,
                    soundOnly: false,
                });
                this.setCalling(false);
            }
        },
        getCKInstance(id) {
            try {
                id = id ? id : this.activeConversation._id;
                const instance = this.$refs[`ckeditor-${id}`].instance || this.$refs[`ckeditor-${id}`].$_instance;
                return instance;
            } catch (e) {
                log.warn("ck instance not found", { id });
                return undefined;
            }
        },
        markBannedWords(msg) {
            if (!this.settings.bannedWordMarking) return msg;
            const clearRegex = /<span class="banned">(\w+)<\/span>/gm;
            const before = msg;
            msg = msg.replaceAll(clearRegex, (match, group) => {
                return group;
            });
            //log.debug("mesut: after",{before,after:msg})
            // msg = msg.replaceAll(/<\/span><\/span>/gm,'</span>')

            this.bannedWords.forEach((bannedWord) => {
                //gerizekalı pgerizekalı gerizekalıp mgerizekalımalt satır gerizekalı pgerizekalı
                bannedWord = bannedWord.trim().toLowerCase();
                const regex = new RegExp(`${bannedWord}`, "gmi");
                /*msg = msg.replaceAll(`<span class="banned">${bannedWord}</span>`, `${bannedWord}`)*/
                msg = msg.replaceAll(regex, (match, index) => {
                    const startIndex = index;
                    const endIndex = index + bannedWord.length - 1;
                    const leftChar = msg[startIndex - 1];
                    const rightChar = msg[endIndex + 1];
                    const isBanned =
                        [undefined, " ", ">", ";"].includes(leftChar) && [undefined, " ", "<", "&"].includes(rightChar);

                    if (isBanned) {
                        //log.debug("mesut: replaceAll",{startIndex,endIndex,leftChar,isBanned,match})
                        return `<span class="banned">${match}</span>`;
                    } else {
                        return match;
                    }
                });
                // msg = msg.replaceAll(regex, `<span class="banned">${bannedWord}</span>`)
            });
            log.debug("markBannedWords after", { after: msg, before });
            return msg;
        },

        log(blob) {
            log("blob", blob);
        },
        cancelCall() {
            publishMessage("vc_ended", { signal: true });
        },
        translate(data) {
            log("translate", data);
            const translateEnabled = this.settings.autoTake;
            if (!translateEnabled) return;
            if (
                ["visitor"].includes(data.messageType) &&
                data.baseLang !== this.user.lang &&
                !data.translates.find((x) => x.to === this.user.lang)
            ) {
                sendSocketMessage("user translate-message", {
                    siteId: this.site._id,
                    to: this.user.lang,
                    data,
                });
            }
        },
        bindKeydownCKEditor() {
            log("bindKeydownCKEditor called");
            try {
                const instance = this.getCKInstance();
                if (instance.keydownBinded) {
                    log.warn("bindKeydownCKEditor already binded");
                    return;
                }
                instance.editing.view.document.on(
                    "keydown",
                    (event, data) => {
                        log("keydown received", event);
                        this.handleSubmit(event, data);
                    },
                    { priority: "high" }
                );
                instance.editing.view.document.on(
                    "paste",
                    async (event, data) => {
                        const files = _.get(data, "dataTransfer.files");
                        if (Array.isArray(files) && files.length > 0) {
                            if (!this.agentFileSendEnabled) {
                                return this.$message.error(this.$t("message.dndBlocked"));
                            }
                            const chat_id = this.activeConversation._id;
                            for (let i = 0; i < files.length; i++) {
                                let uploadFile = {
                                    chat_id: chat_id,
                                    fileNumber: Math.random(),
                                    finished: false,
                                    percent: 0,
                                    failed: false,
                                };
                                await this.uploadFile(files[i], uploadFile);
                            }
                            event.stop();
                        }
                    },
                    { priority: "high" }
                );
                log("keydown bind success");
                this.keydownBinded = true;
                instance.keydownBinded = true;
            } catch (e) {
                log.warn("keydown bind error", e);
                this.keydownBinded = false;
            }
        },
        getHashWord(htmlStr) {
            const parser = new DOMParser();

            let doc = parser.parseFromString(htmlStr, "text/html");

            let hashableWord = _.last(
                doc.body.textContent

                    .split(" ")

                    .map((x) => x.trim())

                    .filter((x) => !!x && x.startsWith("#"))
            );

            log("hashableWord", hashableWord);

            return hashableWord;
        },

        hashWatch(htmlStr) {
            let hashableWord = this.getHashWord(htmlStr);

            this.isHashShortcutMode = !!hashableWord;
            log("hashWatch", { hashableWord, isHashShortcutMode: this.isHashShortcutMode });
            if (!hashableWord) return;

            let searchTxt = hashableWord.replaceAll("#", "");
            const searchedList = this.contents.data.filter((q) => q.type === "HASH");
            let fuzzySearch = new Fuse(searchedList, { keys: ["hash"] });
            this.suggestions = searchTxt.length > 0 ? fuzzySearch.search(searchTxt).map((x) => x.item) : searchedList;
            this.suggestionsCurrent = 0;

            log("hashWatch", {
                hashableWord,
                isHashShortcutMode: this.isHashShortcutMode,
                searchTxt,
                searchedList,
                suggestions: this.suggestions,
            });
            // this.suggestions = this.contents.data
            //
            //     .filter((q) => q.type === "HASH")
            //
            //     .filter((w) =>
            //         hashableWord === "" ? true : new RegExp(hashableWord.replaceAll("#", ""), "gmi").test(w.hash)
            //     );
        },
        addDraftMessageDelayed(conversationId, message, interval = 1000) {
            let timerId = this.draftMessageTimers[conversationId];
            if (timerId) clearTimeout(timerId);
            timerId = setTimeout(() => {
                this.addDraftMessage({
                    conversationId,
                    message,
                });
            }, interval);
            this.cancelTokens.push(() => {
                clearTimeout(timerId);
            });
            if (timerId) this.draftMessageTimers[conversationId] = timerId;
        },
        getMessageFromCKEditor() {
            try {
                return this.getCKInstance(this.prevChatId).ui.editor.getData();
            } catch (e) {
                log.error("getMessageFromCKEditor error", e);
                return "";
            }
        },
        setMessages(conversationId, newMessageList, resyncMode = false) {
            const activeConversationId = _.get(this.activeConversation, "_id");
            if (activeConversationId !== conversationId) {
                log.warn("syncMessages conversationId changed", {
                    conversationId,
                    newConversationId: _.get(this.activeConversation, "_id"),
                    resyncMode,
                });
            } else {
                const addMessages = () => {
                    this.messages = [];
                    this.addMessage({
                        messageType: "load-more",
                        conversationId: conversationId,
                        createdDate: new Date(-8640000000000000),
                        uniqMessageId: "load-more",
                    });
                    this.addMultiMessage(newMessageList);
                };
                if (resyncMode) {
                    const diffList = _.differenceBy(newMessageList, this.messages, "uniqMessageId");
                    log("syncMessages resyncMode", { diffList, old: this.messages, new: newMessageList });
                    if (diffList.length > 0) {
                        log.warn("chatwindow messages are not sync", {
                            newMessageList,
                            oldMessageList: this.messages,
                            diffList,
                        });
                        addMessages();
                    }
                } else {
                    addMessages();
                }
            }
        },

        async syncMessages(conversationId, resyncMode = false, cache = false) {
            const activeConversationId = _.get(this.activeConversation, "_id");
            conversationId = conversationId || activeConversationId;
            if (conversationId !== activeConversationId) {
                log.warn("syncMessages activeConversationId changed", { conversationId, activeConversationId });
                return;
            }
            log("syncMessages", { conversationId, resyncMode });
            if (conversationId) {
                if (!resyncMode) this.messageSyncRunning = true;
                return this.loadOldHistoryMessages({ conversationId, cache })
                    .then((newMessageList) => {
                        this.setMessages(conversationId, newMessageList, resyncMode);
                    })
                    .finally(() => {
                        if (!resyncMode) this.messageSyncRunning = false;
                    });
            }
        },
        listenLoadMore() {
            const conversationId = _.get(this.activeConversation, "_id");
            if (!conversationId) return;
            if (!!this.loadMoreSubscriber) {
                this.loadMoreSubscriber.unsubscribe();
            }
            log("load more subscriber added");
            this.loadMoreSubscriber = SYSTEM_PUBSUB.stream
                .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.ACTIVE_CONVERSATION_LOAD_MORE))
                .subscribe(async () => {
                    log("load more clicked");
                    this.syncMessages(conversationId, true).finally(() => {
                        try {
                            document.getElementsByClassName("sc-message-list")[0].scrollTop = 0;
                        } catch (e) {
                            log.error("scroll error", e);
                        }
                    });
                });
        },
        setupResyncMessageWorker(conversationId) {
            if (this.lastReSyncIntervalId) {
                clearInterval(this.lastReSyncIntervalId);
            }
            const sync = async () => {
                const messages = await this.loadLastNMessage({
                    conversationId,
                    take: 20,
                }).catch((err) => []);
                const currentChatId = _.get(this.activeConversation, "_id");
                if (currentChatId === conversationId) {
                    const diffList = _.differenceBy(messages, this.messages, "uniqMessageId");
                    if (diffList.length > 0) {
                        log.warn("chatwindow messages are not sync", {
                            newMessageList: messages,
                            oldMessageList: this.messages,
                            diffList,
                        });
                        this.addMultiMessage(messages);
                    } else {
                        log("chatwindow messages are sync", {
                            newMessageList: messages,
                            oldMessageList: this.messages,
                            diffList,
                        });
                    }
                }

                this.setMessages(conversationId, messages, true);
            };
            sync();
            const intervalId = setInterval(sync, 3000);
            if (this.lastReSyncIntervalId) clearInterval(this.lastReSyncIntervalId);
            this.lastReSyncIntervalId = intervalId;
            this.cancelTokens.push(() => {
                clearInterval(intervalId);
            });
        },

        getLangContent(source, targetLang) {
            return source[targetLang] || source["tr"] || source[Object.keys(source)[0]];
        },
    },
    created() {
        const sub = socketMessageStream.pipe(filter((x) => ["message"].includes(x.cmd))).subscribe((x) => {
            const { data } = x;
            if (this.activeConversation && this.activeConversation._id !== data.conversationId) {
                log.warn("unrelated message received from different chat", {
                    currentChatId: this.activeConversation._id,
                    otherChatId: data.conversationId,
                });
                return;
            }
            if (data.type === "customer-typing") {
                if (this.settings.writing === 1) {
                    this.typingMessage = data.message;
                }
                return;
            }
            this.translate(data);
            this.addMessage(data);
            if (data.messageType === "visitor") {
                setTimeout(() => {
                    this.typingMessage = "";
                }, 500);
            }
        });
        this.cancelTokens.push(() => sub.unsubscribe());
    },

    updated() {
        if (this.$refs[`ckeditor-${this.activeConversation._id}`] && !this.keydownBinded) {
            setTimeout(() => {
                this.bindKeydownCKEditor();
            }, 100);
        }
        if (this.language.code !== this.$i18n.locale) {
            log("lang diff", this.language.code, this.$i18n.locale);
            this.$i18n.locale = this.language.code;
            this.steps = createChatWindowTourSteps(this);
        }
    },
    watch: {
        speechRecognitionActive(isActive) {
            if (isActive) {
                this.speechRecognition = new SpeechToTextService(this.speechRecognitionContinousMode);
                this.speechRecognition.addEventListener("text", (e) => {
                    this.message = e.detail;
                });
                this.speechRecognition.addEventListener("end", (e) => {
                    const message = e.detail || "";
                    if (message.length) {
                        this.message = e.detail;
                        this.speechRecognition.stop();
                        this.speechRecognition = null;
                        this.speechRecognitionActive = false;
                        this.speechRecognitionContinousMode = false;
                    }
                });
                this.speechRecognition.addEventListener("error", (e) => {
                    this.speechRecognitionActive = false;
                    this.speechRecognitionContinousMode = false;
                    this.speechRecognition.stop();
                    this.speechRecognition = null;
                });
            } else {
                this.speechRecognitionContinousMode = false;
                if (this.speechRecognition) {
                    this.speechRecognition.stop();
                    this.speechRecognition = null;
                }
            }
        },
        newCreatedMailId(newValue) {
            this.newCreatedMailIdClone = newValue;
            this.$message.success("Mail gönderildi.");
        },
        socketConnected(isConnected) {
            log("socketConnected changed", isConnected);
            if (isConnected) {
                if (this.activeConversation) {
                    this.resetChatHistory();
                    this.syncMessages(this.activeConversation._id);
                }
            }
        },
        activeConversation: {
            handler(newValue, oldValue) {
                let message = this.getMessageFromCKEditor();
                log("activeConversation changed", {
                    newValue,
                    oldValue,
                    message,
                });
                if (newValue._id !== oldValue._id) {
                    // this.destroyCKInstance(oldValue._id);
                    this.addDraftMessageDelayed(oldValue._id, message, 0);
                    this.prevChatId = newValue._id;
                    const isDraftExits = !!this.bindDraftMessage(newValue._id);
                    if (!isDraftExits) this.message = "";
                    this.typingMessage = "";
                    this.focusCkEditor();
                    this.syncMessages(newValue._id, false, true);
                    this.keydownBinded = false;
                    this.setupResyncMessageWorker(newValue._id);
                    if (this.speechRecognition) {
                        this.speechRecognitionActive = false;
                        this.speechRecognition.stop();
                        this.speechRecognition = null;
                    }
                }
                if (!!newValue) {
                    this.listenLoadMore();
                }
            },
            deep: true,
        },
        language(newValue) {
            log("language changed");
            this.steps = createChatWindowTourSteps(this);
            this.$forceUpdate();
        },
        waitingTabShortcut(newValue) {
            log("waitingTabShortcut changed", newValue, {
                suggestionsCurrent: this.suggestionsCurrent,
            });
            this.waitingTabShortcut = newValue;
        },
        message(newValue) {
            log("message changed", newValue);
            if (newValue.includes("<span>")) {
                const regex = /<span>|<\/span>/gi;
                this.message = newValue.replace(regex, "");
                this.moveCursorToEnd();
            }
            if (this.settings.agentTyping) {
                this.sendTypingInfo();
            }
            if (!!newValue) {
                this.hashWatch(newValue);
            }
            if (this.activeConversation) {
                this.addDraftMessageDelayed(this.activeConversation._id, newValue);
            }
        },
        isCalling(newValue) {
            if (newValue) {
                if (typeof this.callingSound.loop == "boolean") {
                    this.callingSound.loop = true;
                } else {
                    this.callingSound.addEventListener(
                        "ended",
                        function () {
                            this.currentTime = 0;
                            this.play();
                            console.log("video call ended ", this);
                        },
                        false
                    );
                }
                this.callingSound.play();
            } else {
                this.callingSound.pause();
            }
        },
        chatHistory: {
            handler(newVal, oldVal) {
                const conversationHistory = newVal[this.activeConversation._id];
                if (conversationHistory && conversationHistory.data) {
                    this.setMessages(this.activeConversation._id, conversationHistory.data, true);
                }
            },
            deep: true,
        },
    },
};
</script>
<style lang="scss" scoped>
#smsModal {
    .sms-modal-cancel {
        float: inline-end;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        height: 30px;
        margin-top: -10px;
    }

    .sms-modal-image {
        display: flex;
        margin-bottom: 20px;
    }

    .sms-modal-text {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        color: #181c32;
        margin-bottom: 20px;
        text-align: center;
    }

    .sms-modal-btn {
        display: flex;
        justify-content: center;
        align-items: center;

        .push-btn {
            background: #ffa800;
            border: none;
            outline: none;
            color: #ffffff;
            font-size: 14px;
            font-weight: 500;
            padding: 10px 20px;
            border-radius: 4px;
        }
    }
}

.reply-to-message {
    background: #ffe9cb;
    padding: 5px;
    border-left: 4px solid #ffa800;
    border-radius: 4px;
    margin-top: auto;
    position: absolute;
    bottom: 120px;
    width: calc(100% - 4px);
}

.sendBtnPassive {
    cursor: auto;
}

.message-list-wrapper {
    //typing-mode,observing-mode,mail-mode

    &.mail-enabled {
        &.observing-mode {
            height: calc(100% - 30px);

            &.typing-mode {
                height: calc(100% - 50px);
            }
        }

        &:not(.observing-mode) {
            height: calc(100% - 178px);

            &.typing-mode {
                height: calc(100% - 198px);
            }

            &.mail-mode {
                height: calc(100% - 200px);

                &.typing-mode {
                    height: calc(100% - 220px);
                }
            }
        }
    }

    &:not(.mail-enabled) {
        &.observing-mode {
            height: calc(100% - 30px);

            &.typing-mode {
                height: calc(100% - 50px);
            }
        }

        &:not(.observing-mode) {
            height: calc(100% - 148px);

            &.typing-mode {
                height: calc(100% - 168px);
            }
        }
    }
}

.new-theme {
    .ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 200px;
    }

    .video-call-action-button {
        background: none;
        border: none;
        padding: 0;
        margin: 0 12px 0 0;
        outline: none;
    }

    #chatWindow {
        .row {
            margin: 0;
        }

        .dnd {
            background-image: linear-gradient(90deg, silver 50%, transparent 50%),
                linear-gradient(90deg, silver 50%, transparent 50%), linear-gradient(0deg, silver 50%, transparent 50%),
                linear-gradient(0deg, silver 50%, transparent 50%);
            background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
            background-size: 15px 2px, 15px 2px, 2px 15px, 2px 15px;
            background-position: left top, right bottom, left bottom, right top;
            animation: border-dance 1s infinite linear;
            display: flex;
            justify-content: center;
            align-items: center;
            height: calc(100vh - 325px) !important;
        }

        @keyframes border-dance {
            0% {
                background-position: left top, right bottom, left bottom, right top;
            }
            100% {
                background-position: left 15px top, right 15px bottom, left bottom 15px, right top 15px;
            }
        }

        .chatContainer {
            border-radius: 6px;
            box-shadow: 0 0 3px #00000080;
            height: calc(100vh - 140px);
            overflow: hidden;
            position: relative;
        }
    }

    #chatWindowCloseHideButton {
        display: flex;
        justify-content: flex-end;
        padding-bottom: 2px;
    }

    #chatCloseSettingsButton {
        display: flex;
        justify-content: space-between;
        padding: 10px 10px 10px 10px;
        background-color: white;
        align-items: center;
        height: 30px;
    }

    .cursorPointer {
        cursor: pointer;
    }

    .cursorPointer:hover {
        font-weight: 600;
    }

    .popoverFont {
        font-family: Poppins;
        font-size: 14px;
        color: #181c32;
        font-weight: 300;
    }

    .text-danger {
        color: #afafaf !important;
    }
}

.emoji-icon-container {
    position: absolute;
    top: 7px;
    left: 114px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.voice-container {
    position: absolute;
    top: 1px;
    left: 142px;
    display: flex;
    width: max-content;
    align-items: center;
    margin-right: 5px;
    height: 36px;

    #voiceRecorder {
        width: max-content;

        .actionBtn {
            width: 15px;
            margin-right: 3px;
        }
    }
}

.emoji-button {
    z-index: 3;
    cursor: pointer;
}

.dark-mode {
    .darkSpan {
        color: white !important;
    }

    .ant-avatar {
        background: #333440 !important;
    }
}

.translate-detected {
    padding: 0.6rem;
    z-index: 999 !important;
    background: #ffe6c1;
    width: 100%;
    position: absolute;
    color: #ffa82a;
    cursor: pointer;
}
</style>
<style lang="scss">
//#chat-page .ck.ck-content.ck-editor__editable {
//    min-height: 125px;
//}
//.ck.ck-reset.ck-editor.ck-rounded-corners {
//    height: 200px;
//}
//#frmChat {
//    height: 200px !important;
//    margin-top: -70px !important;
//}

.uploading-wrapper {
    background: white;
    justify-content: center;
    display: flex;
    flex-wrap: wrap;

    .ant-progress-line {
        justify-content: center;
        display: flex;

        .ant-progress-inner {
            width: 350px;

            .ant-progress-bg {
                width: 350px;
            }
        }
    }
}

.naked-btn {
    outline: none;
    border: none;
    background-color: transparent;
}

.mail-subject {
    margin-top: -26px;
    background-color: white;
    z-index: 999;
    position: relative;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    height: 25px;
    padding-left: 5px;

    .subject-input {
        border: none;
        width: 100%;
    }
}

.send-types {
    display: flex;
    background-color: white;
    padding-left: 10px;
    border-top: 1px solid #4f8cff;

    .type {
        display: flex;
        align-items: center;
        padding: 5px;
        cursor: pointer;

        &.selected {
            border-radius: 5px;
            background-color: #e5f5ff;
        }

        &:hover {
            border-radius: 5px;
            background-color: #e5f5ff;
        }
    }

    .icon {
        color: #1890ff;
        font-size: 17px;
        margin-right: 3px;
    }

    .send-type {
        font-size: 13px;
        font-weight: 500;
        color: #1890ff;
    }
}

.message.typing {
    min-height: 20px;
    background: white;
    color: #1e1e2d;
    width: calc(100%);
}

.message.typing.live {
    background: #e4f8fe;
    -webkit-line-clamp: 1;
    text-overflow: ellipsis;
    max-height: 20px;
    word-break: break-all;
}

input.visitorName[placeholder]:empty::before {
    content: attr(placeholder);
    color: #555;
}

input.visitorName[placeholder]:empty:focus::before {
    content: "";
}

#visitorName {
    margin-right: 10px;
    cursor: default;
}

#visitorEmail {
    cursor: default;
}
</style>
